import { useSetState } from '@/shared/lib/customHooks';
import { useEffect, useRef } from 'react';
import { userPreferences } from '@/entities/user';
import type { EcgOptionsComponentProps } from './ecgOptionsComponent.types';

/**
 * Drives the ECG viewer guideline notice in the ECG options bar.
 * Opens the notice unless the user already dismissed it for good, tracks the do-not-show-again
 * checkbox, and clears the initial-render ref once the ECG data has loaded.
 *
 * @param props - Options props carrying isLoadedData.
 * @returns The notice state, the initial-render ref and the checkbox and confirm handlers.
 */
export const useEcgOptionsComponent = ({
  isLoadedData,
}: Pick<EcgOptionsComponentProps, 'isLoadedData'>) => {
  const isInitial = useRef(true);

  const [state, setState] = useSetState({
    isOpenNotiPopover: !userPreferences.isCheckedViewEcgGuideLineNoti(),
    isCheck: false,
  });

  /**
   * Toggles the do-not-show-again checkbox of the ECG viewer guideline notice. Flips state.isCheck to its opposite value.
   *
   * @returns Nothing.
   */
  const onChangeCheckbox = () => {
    setState({ isCheck: !state.isCheck });
  };

  /**
   * Dismisses the ECG viewer guideline notice popover. Persists the acknowledgement through userPreferences.setIsCheckedViewEcgGuideLineNoti when the checkbox is ticked, then closes the popover.
   *
   * @returns Nothing.
   */
  const onClickConfirm = () => {
    if (state.isCheck) {
      userPreferences.setIsCheckedViewEcgGuideLineNoti(true);
    }
    setState({ isOpenNotiPopover: false });
  };

  useEffect(() => {
    if (isLoadedData) {
      isInitial.current = false;
    }
  }, [isLoadedData]);

  return { state, isInitial, onChangeCheckbox, onClickConfirm };
};
